import React, { useEffect, useMemo, useState, useRef } from "react";
import { isPracticeId } from "../../../../onboarding/practice/practiceGuards";
import { useTranslation } from "react-i18next";
import DishTable from "./DishTable";
import Pagination from "./Pagination";
import { DishRowData } from "../costPriceDataTypes";
import { TabId } from "./Header";
import { RevenueSourceType } from "../../../../../types/RevenueSourceType";
import { useAppUserDefaultStore, loadTableItemsPerPageSetting, saveTableItemsPerPageSetting } from "../../../../../stores/appUserDefaultStore";
import { ChevronDown } from "lucide-react";
import { CostPriceCategory } from "../../../../../services/costPrice/costPriceCategoriesService";
import { Menu } from "../../../../../services/menus/menusService";
import { CostTargets } from "../../../../../utils/costTargets";
import MenuFilterDropdown from "../../../../menus/MenuFilterDropdown";
import { horizontalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToHorizontalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { SortableList } from "../../../../common/SortableList";
import { cn } from "../../../../../lib/utils";
import { elevatedBg, primaryText, secondaryText } from "../../../../../styling/stylingHelper";
import { ResponsiveTable, MobileCardList, MobileCardField } from "../../../../shared/mobile/ResponsiveTable";
import { CategoryBadge } from "../../../../../utils/categoryHelper";
import { getCostTargetForDishType, isCostBad as isCostBadFor, DEFAULT_COST_TARGETS } from "../../../../../utils/costTargets";

// Elevated card surface for the mobile card list (matches the shipped OrderHistory/Revenue cards).
const MOBILE_CARD =
    'bg-white dark:bg-card-dark rounded-xl p-4 shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]';

// How many mobile cards to reveal per "Load more" tap (never render the whole list at once).
const MOBILE_BATCH = 20;

const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(value);

interface CostPriceTableProps {
    data: DishRowData[];
    activeTab: TabId;
    selectedCategoryId: string;
    onCategoryChange: (categoryId: string) => void;
    onDishClick: (dish: DishRowData) => void;
    onCategoryClick?: (dish: DishRowData) => void;
    matrixFilters: string[];
    onMatrixFiltersChange: (filters: string[]) => void;
    sortColumn: string | null;
    sortDirection: 'asc' | 'desc' | null;
    onSort: (column: string) => void;
    filteredCategories: CostPriceCategory[]; // Categories filtered by active tab
    onReorderCategories?: (reordered: CostPriceCategory[]) => Promise<void>;
    menus?: Menu[];
    menuFilters: string[];
    onMenuFiltersChange: (next: string[]) => void;
    isMenuFilterActive: boolean;
    matchesMenuFilter: ((row: DishRowData) => boolean) | null;
    costTargets?: CostTargets;
    // Reports the rows currently shown (all filters + search + sorting applied,
    // across all pages) so the page-level export/copy actions use the exact
    // same data source as the table.
    onVisibleRowsChange?: (rows: DishRowData[]) => void;
}

interface SortableCategoryTabProps {
    category: CostPriceCategory;
    isActive: boolean;
    onClick: () => void;
    draggable: boolean;
}

const SortableCategoryTab: React.FC<SortableCategoryTabProps> = ({ category, isActive, onClick, draggable }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: category.id,
        disabled: !draggable,
    });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <button
            ref={setNodeRef}
            style={style}
            type="button"
            onClick={onClick}
            {...(draggable ? attributes : {})}
            {...(draggable ? listeners : {})}
            className={
                "px-3 py-2.5 text-nowrap text-[14px] leading-5 font-semibold border-l border-[#E5E7EB] dark:border-borderPrimary-dark touch-none " +
                (isActive
                    ? "bg-[#EDFCF5] dark:bg-[#0E8965]/20 text-[#0E8965]"
                    : "bg-white dark:bg-background-dark text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-neutral-800")
            }
        >
            <div className="flex items-center gap-2">
                {category.name}
            </div>
        </button>
    );
};

const CostPriceTable: React.FC<CostPriceTableProps> = ({ data, activeTab, selectedCategoryId, onCategoryChange, onDishClick, onCategoryClick, matrixFilters, onMatrixFiltersChange, sortColumn, sortDirection, onSort, filteredCategories, onReorderCategories, menus, menuFilters, onMenuFiltersChange, isMenuFilterActive, matchesMenuFilter, costTargets, onVisibleRowsChange }) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");
    // The tour's listeners are registered once, so they read the latest value from here.
    const searchRef = useRef(search);
    searchRef.current = search;
    const [currentPage, setCurrentPage] = useState(1);
    const [isItemsPerPageDropdownOpen, setIsItemsPerPageDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Placeholder toggle state - default to closed when a menu is selected
    const [showPlaceholders, setShowPlaceholders] = useState(true);
    const [showIrrelevant, setShowIrrelevant] = useState(false);
    const restaurantId = useAppUserDefaultStore(s => s.restaurant?.id);

    // Keep the choice while browsing filters, but start hidden for each restaurant.
    useEffect(() => {
        setShowIrrelevant(false);
    }, [restaurantId]);

    // Mobile-only: how many dish cards are rendered (grows on "Load more").
    // The desktop table paginates internally; on mobile we window the same array
    // so we never render thousands of cards at once (Conversion Playbook → Large lists).
    const [mobileVisible, setMobileVisible] = useState(MOBILE_BATCH);

    // Get items per page from user defaults store
    const itemsPerPage = useAppUserDefaultStore(s => s.tableItemsPerPage);

    // Load items per page setting on component mount
    useEffect(() => {
        loadTableItemsPerPageSetting();
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsItemsPerPageDropdownOpen(false);
            }
        };

        if (isItemsPerPageDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isItemsPerPageDropdownOpen]);

    // Stable key for filter selection (used to reset page / placeholder visibility on change)
    const menuFiltersKey = menuFilters.join('|');

    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, search, selectedCategoryId, itemsPerPage, matrixFilters, sortColumn, sortDirection, menuFiltersKey, showPlaceholders, showIrrelevant, restaurantId]);

    // Reset placeholder visibility when the menu filter changes
    useEffect(() => {
        setShowPlaceholders(!isMenuFilterActive);
    }, [isMenuFilterActive, menuFiltersKey]);

    // Filter data by tab, category, and search (before matrix filter)
    // This is used to determine available matrix options
    const preMatrixFilteredData = useMemo(() => {
        return data.filter((row) => {
            // Filter by menu (keep placeholders accessible for easy creation)
            if (matchesMenuFilter) {
                const isPlaceholder = row.isPlaceholder || row.isUncategorizedPlaceholder;
                if (!isPlaceholder && !matchesMenuFilter(row)) {
                    return false;
                }
            }

            // Filter by tab (All / Food / Drinks)
            if (activeTab === "food") {
                if (!row.revenueSourceType || row.revenueSourceType !== RevenueSourceType.FOOD) {
                    return false;
                }
            } else if (activeTab === "drinks") {
                if (!row.revenueSourceType || row.revenueSourceType !== RevenueSourceType.DRINKS) {
                    return false;
                }
            }
            // For "all" tab, show everything (revenue items + custom items)

            // Filter by category
            if (selectedCategoryId !== "all") {
                if (row.categoryId !== selectedCategoryId) {
                    return false;
                }
            }

            // Filter by search — match dish name OR any ingredient/product name in the dish
            const query = search.trim().toLowerCase();
            if (!query) {
                return true;
            }
            // Match the DISPLAY name too, so a partner venue ("bar pop") finds its dishes.
            const matchDishName = (row.displayName || row.dishName).toLowerCase().includes(query);
            const matchIngredient = row.ingredients?.some((ingredient) =>
                [
                    ingredient.productName,
                    ingredient.customPlatformName,
                    ingredient.subrecipeName,
                    ingredient.nestedCostPriceName,
                ].some((name) => name?.toLowerCase().includes(query))
            );
            return matchDishName || Boolean(matchIngredient);
        });
    }, [data, activeTab, selectedCategoryId, search, matchesMenuFilter]);

    // Apply matrix filter to the pre-filtered data
    const matrixFilteredData = useMemo(() => {
        return preMatrixFilteredData.filter((row) => {
            // Hide placeholders when a menu filter is active and toggle is off
            if (isMenuFilterActive && !showPlaceholders) {
                if (row.isPlaceholder || row.isUncategorizedPlaceholder) {
                    return false;
                }
            }

            // Filter by matrix (multi-select)
            if (matrixFilters.length > 0) {
                // If row has no matrix (null), exclude it when filters are active
                if (row.matrix === null) {
                    return false;
                }
                // Row has a matrix value, check if it's in the selected filters
                if (!matrixFilters.includes(row.matrix)) {
                    return false;
                }
            }
            return true;
        });
    }, [preMatrixFilteredData, matrixFilters, isMenuFilterActive, showPlaceholders]);

    const irrelevantCount = useMemo(() =>
        matrixFilteredData.filter(row => row.markedIrrelevant).length,
    [matrixFilteredData]);

    const filteredData = useMemo(() => {
        let filtered = matrixFilteredData.filter(row => showIrrelevant || !row.markedIrrelevant);

        // Sort: When showing "all" categories, show categorized items first, then uncategorized placeholders
        if (selectedCategoryId === "all") {
            filtered = filtered.sort((a, b) => {
                // Uncategorized placeholders go to the end
                if (a.isUncategorizedPlaceholder && !b.isUncategorizedPlaceholder) return 1;
                if (!a.isUncategorizedPlaceholder && b.isUncategorizedPlaceholder) return -1;
                return 0; // Keep original order for items in the same category
            });
        }

        // Apply column sorting if specified
        if (sortColumn && sortDirection) {
            filtered = filtered.sort((a, b) => {
                // Pure alphabetical sort for dish name — no "send to end" rule.
                if (sortColumn === 'dishName') {
                    const comparison = a.dishName.localeCompare(b.dishName);
                    return sortDirection === 'asc' ? comparison : -comparison;
                }

                // Margin can be negative or 0, so the "0 goes last" rule below doesn't fit.
                // Rows the table shows "-" for (add-ons, placeholders, no price) go last instead.
                if (sortColumn === 'margin') {
                    const hasNoMargin = (row: DishRowData) => row.isChoiceCost || row.isPlaceholder || row.totalCost === 0 || row.price === 0;
                    if (hasNoMargin(a) && hasNoMargin(b)) return 0;
                    if (hasNoMargin(a)) return 1;
                    if (hasNoMargin(b)) return -1;
                    const comparison = a.margin - b.margin;
                    return sortDirection === 'asc' ? comparison : -comparison;
                }

                let aValue: number;
                let bValue: number;

                switch (sortColumn) {
                    case 'cost':
                        aValue = a.totalCost || 0;
                        bValue = b.totalCost || 0;
                        break;
                    case 'price':
                        aValue = a.price || 0;
                        bValue = b.price || 0;
                        break;
                    case 'costPercentage':
                        aValue = a.costPercentage || 0;
                        bValue = b.costPercentage || 0;
                        break;
                    case 'popularity':
                        aValue = a.popularity || 0;
                        bValue = b.popularity || 0;
                        break;
                    default:
                        return 0;
                }

                // Handle null/undefined values - put them at the end
                if (aValue === 0 && bValue === 0) return 0;
                if (aValue === 0) return 1;
                if (bValue === 0) return -1;

                const comparison = aValue - bValue;
                return sortDirection === 'asc' ? comparison : -comparison;
            });
        }

        return filtered;
    }, [matrixFilteredData, sortColumn, sortDirection, selectedCategoryId, showIrrelevant]);

    // Publish the currently visible rows upwards (export / copy to clipboard).
    useEffect(() => {
        onVisibleRowsChange?.(filteredData);
    }, [filteredData, onVisibleRowsChange]);

    // Reset the mobile window whenever the filtered/sorted result set changes.
    useEffect(() => {
        setMobileVisible(MOBILE_BATCH);
    }, [filteredData]);

    // Count placeholders for toggle (only relevant when a menu filter is active)
    const placeholderCount = useMemo(() => {
        if (!isMenuFilterActive) return 0;
        return preMatrixFilteredData.filter(row => row.isPlaceholder || row.isUncategorizedPlaceholder).length;
    }, [preMatrixFilteredData, isMenuFilterActive]);

    // Handler for changing items per page
    const handleItemsPerPageChange = async (newItemsPerPage: number) => {
        try {
            await saveTableItemsPerPageSetting(newItemsPerPage);
            setIsItemsPerPageDropdownOpen(false);
        } catch (error) {
            console.error('Failed to save items per page setting:', error);
        }
    };

    const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));

    // Guided tour: bring a named row onto the visible page before the step spotlights it.
    useEffect(() => {
        const onReveal = (event: Event) => {
            const id = (event as CustomEvent<{ id?: string }>).detail?.id;
            if (!id || !isPracticeId(id)) return;
            const index = filteredData.findIndex(row => row.id === id);
            if (index >= 0) setCurrentPage(Math.floor(index / itemsPerPage) + 1);
        };
        // A leftover table search would hide the practice row the next step points at,
        // so the tour asks for it, clears it, and hands it back when the branch ends.
        const onSnapshot = (event: Event) => {
            const detail = (event as CustomEvent<{ search?: string }>).detail;
            if (detail) detail.search = searchRef.current;
        };
        const onResetFilters = () => setSearch('');
        const onRestoreFilters = (event: Event) => {
            const previous = (event as CustomEvent<{ search?: string }>).detail?.search;
            if (previous) setSearch(previous);
        };
        window.addEventListener('onboarding:cost-reveal-dish', onReveal);
        window.addEventListener('onboarding:cost-snapshot-filters', onSnapshot);
        window.addEventListener('onboarding:cost-reset-filters', onResetFilters);
        window.addEventListener('onboarding:cost-restore-filters', onRestoreFilters);
        return () => {
            window.removeEventListener('onboarding:cost-reveal-dish', onReveal);
            window.removeEventListener('onboarding:cost-snapshot-filters', onSnapshot);
            window.removeEventListener('onboarding:cost-reset-filters', onResetFilters);
            window.removeEventListener('onboarding:cost-restore-filters', onRestoreFilters);
        };
    }, [filteredData, itemsPerPage]);
    // Marking the last dish on a page can remove that page while its detail is open.
    const visiblePage = Math.min(currentPage, totalPages);
    useEffect(() => {
        setCurrentPage(page => Math.min(page, totalPages));
    }, [totalPages]);
    const startIndex = (visiblePage - 1) * itemsPerPage;
    const pageRows = filteredData.slice(startIndex, startIndex + itemsPerPage);

    // Calculate showing x-y of z items
    const totalItems = filteredData.length;
    const showingFrom = totalItems === 0 ? 0 : startIndex + 1;
    const showingTo = totalItems === 0 ? 0 : Math.min(startIndex + itemsPerPage, totalItems);

    return (
        <div className="mt-6 bg-white dark:bg-background-dark rounded-xl pt-4 sm:pt-5 pb-4 border border-transparent dark:border-borderPrimary-dark" data-onboarding="cost-table">
            {/* Header + tabs + search */}
            <div className="flex items-center justify-between mb-4 sm:mb-5 gap-4 flex-wrap min-[1050px]:flex-nowrap px-4">
                <h3 className="text-[16px] font-semibold text-[#111827] dark:text-textPrimary-dark whitespace-nowrap flex-shrink-0">
                    {t('costPrice.dishName') || 'Dish Name'}
                </h3>

                <div className="flex items-center gap-4 sm:gap-3 flex-wrap min-[1050px]:flex-nowrap overflow-hidden">
                    {/* Category Tabs */}
                    {filteredCategories.length > 0 && (
                        <div className="hide-scrollbar inline-flex rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark overflow-auto bg-white dark:bg-background-dark flex-1 min-w-0">
                            <button
                                type="button"
                                onClick={() => onCategoryChange("all")}
                                className={
                                    "px-3 py-2.5 text-nowrap text-[14px] leading-5 font-semibold " +
                                    (selectedCategoryId === "all"
                                        ? "bg-[#EDFCF5] dark:bg-[#0E8965]/20 text-[#0E8965]"
                                        : "bg-white dark:bg-background-dark text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-neutral-800")
                                }
                            >
                                All
                            </button>
                            {onReorderCategories ? (
                                <SortableList
                                    items={filteredCategories.filter(c => !c.isDefault)}
                                    getId={(cat) => cat.id}
                                    onReorder={onReorderCategories}
                                    strategy={horizontalListSortingStrategy}
                                    modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
                                >
                                    {filteredCategories.filter(c => !c.isDefault).map((category) => (
                                        <SortableCategoryTab
                                            key={category.id}
                                            category={category}
                                            isActive={selectedCategoryId === category.id}
                                            onClick={() => onCategoryChange(category.id)}
                                            draggable={true}
                                        />
                                    ))}
                                </SortableList>
                            ) : (
                                filteredCategories.filter(c => !c.isDefault).map((category) => (
                                    <SortableCategoryTab
                                        key={category.id}
                                        category={category}
                                        isActive={selectedCategoryId === category.id}
                                        onClick={() => onCategoryChange(category.id)}
                                        draggable={false}
                                    />
                                ))
                            )}
                            {filteredCategories.filter(c => c.isDefault).map((category) => (
                                <SortableCategoryTab
                                    key={category.id}
                                    category={category}
                                    isActive={selectedCategoryId === category.id}
                                    onClick={() => onCategoryChange(category.id)}
                                    draggable={false}
                                />
                            ))}
                        </div>
                    )}

                    {/* Menu Filter (always shown so users can pick "Active Dishes" even with no menus) */}
                    <MenuFilterDropdown
                        menus={menus || []}
                        selected={menuFilters}
                        onChange={onMenuFiltersChange}
                    />

                    {/* Search */}
                    <div className="relative w-full sm:w-[191px] flex-shrink-0">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] dark:text-textTertiary-dark text-sm">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('costPrice.searchDishOrIngredient') || 'Search dish or ingredient'}
                            className="w-full pl-9 pr-3 py-2 rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark bg-white dark:bg-background-dark text-[16px] leading-6 text-[#111827] dark:text-textPrimary-dark placeholder:text-[#667085] dark:placeholder:text-textTertiary-dark outline-none"
                        />
                    </div>
                </div>
            </div>

            {/* Compact visibility controls below the category filters. */}
            {(placeholderCount > 0 || irrelevantCount > 0) && (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 mb-3">
                    {placeholderCount > 0 && (
                        <button
                            onClick={() => setShowPlaceholders(!showPlaceholders)}
                            className="flex items-center gap-2 text-[13px] text-[#667085] dark:text-textTertiary-dark hover:text-[#344054] dark:hover:text-textSecondary-dark transition-colors"
                            type="button"
                        >
                            <ChevronDown className={`w-4 h-4 transition-transform ${!showPlaceholders ? '-rotate-90' : ''}`} />
                            <span>
                                {showPlaceholders
                                    ? t('costPrice.hidePlaceholders')
                                    : t('costPrice.showPlaceholders', { count: placeholderCount })}
                            </span>
                        </button>
                    )}
                    {irrelevantCount > 0 && (
                        <button
                            onClick={() => setShowIrrelevant(value => !value)}
                            aria-expanded={showIrrelevant}
                            className="flex items-center gap-2 text-[13px] text-[#667085] dark:text-textTertiary-dark hover:text-[#344054] dark:hover:text-textSecondary-dark transition-colors"
                            type="button"
                        >
                            <ChevronDown className={`w-4 h-4 transition-transform ${!showIrrelevant ? '-rotate-90' : ''}`} />
                            <span>
                                {t(showIrrelevant ? 'costPrice.hideIrrelevant' : 'costPrice.showIrrelevant', { count: irrelevantCount })}
                            </span>
                        </button>
                    )}
                </div>
            )}

            <ResponsiveTable
                desktop={
                    <DishTable
                        rows={pageRows}
                        onDishClick={onDishClick}
                        onCategoryClick={onCategoryClick}
                        matrixFilters={matrixFilters}
                        onMatrixFiltersChange={onMatrixFiltersChange}
                        allData={preMatrixFilteredData}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={onSort}
                        costTargets={costTargets}
                    />
                }
                mobile={
                    filteredData.length === 0 ? (
                        <div className="px-4 pt-6 text-center text-[13px] text-[#6B7280] dark:text-textTertiary-dark">
                            {t('costPrice.table.noItemsFound')}
                        </div>
                    ) : (
                        <MobileCardList
                            className="px-4 pt-2"
                            items={filteredData.slice(0, mobileVisible)}
                            keyOf={(row) => row.id}
                            onLoadMore={() => setMobileVisible((v) => v + MOBILE_BATCH)}
                            hasMore={mobileVisible < filteredData.length}
                            loadMoreLabel={t('costPrice.loadMore')}
                            renderCard={(row) => {
                                const rowTarget = getCostTargetForDishType(row.revenueSourceType, costTargets ?? DEFAULT_COST_TARGETS);
                                const isCostBad = isCostBadFor(row.costPercentage, rowTarget);
                                const isPlaceholder = row.isPlaceholder || row.totalCost === 0;
                                const showCostBadge = !row.isChoiceCost && !isPlaceholder && row.price !== 0;
                                return (
                                    <div
                                        className={cn(MOBILE_CARD, 'flex flex-col gap-3 cursor-pointer', (row.isUncategorizedPlaceholder || row.markedIrrelevant) && 'opacity-60')}
                                        onClick={() => onDishClick && onDishClick(row)}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex flex-col gap-1 min-w-0">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className={cn('text-[14px] leading-5 font-medium truncate', primaryText)} title={row.displayName || row.dishName}>{row.displayName || row.dishName}</span>
                                                    {row.markedIrrelevant && (
                                                        <span className={cn('shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] leading-4 font-medium', elevatedBg, secondaryText)}>
                                                            {t('costPrice.notRelevant')}
                                                        </span>
                                                    )}
                                                </div>
                                                <CategoryBadge
                                                    categoryName={row.category}
                                                    categoryColor={row.categoryColor}
                                                    isDefaultCategory={row.isDefaultCategory}
                                                />
                                            </div>
                                            {showCostBadge && (
                                                <span
                                                    className={cn(
                                                        'inline-flex items-center rounded-md px-2 py-1 text-[13px] leading-4 font-semibold flex-shrink-0',
                                                        isCostBad
                                                            ? 'bg-[#FFF4E6] dark:bg-[#FFF4E6]/20 text-[#eb8d00] dark:text-[#eb8d00]'
                                                            : 'bg-[#E7FBF5] dark:bg-[#E7FBF5]/20 text-[#069A6E] dark:text-[#16B364]'
                                                    )}
                                                >
                                                    {row.costPercentage.toFixed(1)}%
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-stretch border-t border-border dark:border-border-dark pt-3">
                                            <MobileCardField
                                                label={t('costPrice.table.cost')}
                                                value={row.isChoiceCost && row.totalCost === 0 ? '--' : isPlaceholder ? '-' : formatCurrency(row.totalCost)}
                                                divider
                                            />
                                            <MobileCardField
                                                label={t('costPrice.table.price')}
                                                value={row.isChoiceCost ? '--' : row.price === 0 ? '-' : formatCurrency(row.price)}
                                                divider
                                            />
                                            <MobileCardField
                                                label={t('costPrice.table.margin')}
                                                value={row.isChoiceCost || isPlaceholder || row.price === 0 ? '-' : formatCurrency(row.margin)}
                                            />
                                        </div>
                                    </div>
                                );
                            }}
                        />
                    )
                }
            />

            <div className="mt-4 sm:mt-3 hidden sm:flex items-center justify-between px-4 sm:px-6">
                <div className="text-[#858D9D] dark:text-textTertiary-dark text-[14px] leading-5 font-medium sm:flex hidden items-center gap-1">
                    <span>{t('common.showing')} {showingFrom}-</span>
                    <div className="relative inline-block" ref={dropdownRef}>
                        <button
                            onClick={() => setIsItemsPerPageDropdownOpen(!isItemsPerPageDropdownOpen)}
                            className="inline-flex items-center gap-0.5 hover:text-[#1E7E34] dark:hover:text-[#1E7E34] transition-colors cursor-pointer focus:outline-none focus:text-[#1E7E34] dark:focus:text-[#1E7E34]"
                            type="button"
                        >
                            <span className="font-semibold">{showingTo}</span>
                            <ChevronDown className="w-3 h-3" />
                        </button>
                        {isItemsPerPageDropdownOpen && (
                            <div className="absolute left-0 bottom-full mb-1 bg-white dark:bg-background-dark border border-[#D0D5DD] dark:border-borderPrimary-dark rounded-lg shadow-lg py-1 z-50 min-w-[80px]">
                                {[10, 15, 50, 100].map((option) => (
                                    <button
                                        key={option}
                                        onClick={() => handleItemsPerPageChange(option)}
                                        className={`w-full px-4 py-2 text-left hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors text-[14px] ${
                                            option === itemsPerPage
                                                ? 'font-semibold text-[#1E7E34]'
                                                : 'font-normal text-[#858D9D] dark:text-textTertiary-dark'
                                        }`}
                                        type="button"
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <span>{t('common.of')} {totalItems}</span>
                </div>
                <Pagination
                    currentPage={visiblePage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            </div>
        </div>
    );
};

export default CostPriceTable;
